import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

export const useCampaignsStore = defineStore('campaign', () => {
  const campaigns = ref([])
  const activeCampaign = ref(null)
  const loading = ref(false)

  async function fetchByGroup(groupId, type = null) {
    loading.value = true
    try {
      const params = type ? { type } : {}
      const { data } = await client.get(`/campaigns/group/${groupId}`, { params })
      campaigns.value = data.content ?? data
      return campaigns.value
    } finally {
      loading.value = false
    }
  }

  async function fetchById(campaignId) {
    loading.value = true
    try {
      const { data } = await client.get(`/campaigns/${campaignId}`)
      activeCampaign.value = data
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchItems(campaignId) {
    const { data } = await client.get(`/campaigns/${campaignId}/items`)
    return data
  }

  async function createCampaign(payload) {
    const { data } = await client.post('/campaigns', payload)
    campaigns.value.unshift(data)
    return data
  }

  async function updateCampaign(campaignId, payload) {
    const { data } = await client.patch(`/campaigns/${campaignId}`, payload)
    activeCampaign.value = data
    const index = campaigns.value.findIndex((c) => c.id === data.id)
    if (index !== -1) campaigns.value[index] = data
    return data
  }

  async function updateContributionSettings(campaignId, payload) {
    const { data } = await client.put(`/campaigns/${campaignId}/contribution-settings`, payload)
    activeCampaign.value = data
    const index = campaigns.value.findIndex((c) => c.id === data.id)
    if (index !== -1) campaigns.value[index] = data
    return data
  }

  async function activate(id) {
    const { data } = await client.post(`/campaigns/${id}/activate`)
    return data
  }

  async function pause(id) {
    const { data } = await client.post(`/campaigns/${id}/pause`)
    return data
  }

  async function cancel(id, reason) {
    const { data } = await client.post(`/campaigns/${id}/cancel`, null, {
      params: { reason },
    })
    return data
  }

  // ── New Progressive Capability Methods ────────────────────────────────

  async function assessPublishReadiness(campaignId) {
    const { data } = await client.get(`/campaigns/${campaignId}/publish-readiness`)
    return data
  }

  async function updateCapabilityFlags(campaignId, flags) {
    const { data } = await client.put(`/campaigns/${campaignId}/capability-flags`, flags)
    activeCampaign.value = data
    const index = campaigns.value.findIndex((c) => c.id === data.id)
    if (index !== -1) campaigns.value[index] = data
    return data
  }

  async function setCapabilityFlag(campaignId, flag, value) {
    const { data } = await client.put(`/campaigns/${campaignId}/capability-flags/${flag}`, null, {
      params: { value }
    })
    activeCampaign.value = data
    const index = campaigns.value.findIndex((c) => c.id === data.id)
    if (index !== -1) campaigns.value[index] = data
    return data
  }

  async function markReadyToPublish(campaignId) {
    const { data } = await client.post(`/campaigns/${campaignId}/mark-ready-to-publish`)
    activeCampaign.value = data
    const index = campaigns.value.findIndex((c) => c.id === data.id)
    if (index !== -1) campaigns.value[index] = data
    return data
  }

  async function publishCampaign(campaignId) {
    const { data } = await client.post(`/campaigns/${campaignId}/publish`)
    activeCampaign.value = data
    const index = campaigns.value.findIndex((c) => c.id === data.id)
    if (index !== -1) campaigns.value[index] = data
    return data
  }

  return {
    campaigns, activeCampaign, loading,
    fetchByGroup, fetchById, fetchItems,
    createCampaign, updateCampaign, updateContributionSettings, activate, pause, cancel,
    assessPublishReadiness, updateCapabilityFlags, setCapabilityFlag, markReadyToPublish, publishCampaign,
  }
})
